<script setup lang="ts">
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { UseStoreUser } from '@/store/userStore';
import { useTipStore } from '@/store/tipStore';
import blogService from '@/services/blogService';
import type { BlogResponse } from '@/types/api';

const { t, locale } = useI18n();
const userStore = UseStoreUser();
const tipStore = useTipStore();
const blogs = ref<BlogResponse[]>([]);
let loading = false;

function loadBlogs() {
  loading = true;
  const lang = locale.value?.slice(0, 2) || 'fr';
  blogService.getLatestBlog(lang).then(result => {
    blogs.value = result ? [result] : [];
    //TODO: remove if tips are no longer displayed
    if (!blogs.value.length) {
      tipStore.loadRandomTip(userStore.userApps.map(app => app.label));
    }
    loading = false;
  });
}

tipStore.setLanguage(locale.value);
loadBlogs();

watch(
  () => locale.value,
  (newLocale) => {
    if (loading) return;
    tipStore.setLanguage(newLocale);
    const lang = newLocale?.slice(0, 2) || 'fr';
    if (blogs.value.length > 0) {
      blogService.getBlogById(blogs.value[0].id, lang).then(result => {
        if (result) blogs.value = [result];
      });
    } else {
      loadBlogs();
    }
  }
);

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return new Intl.DateTimeFormat(locale.value || 'fr', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(d);
}

function todayKey(): string {
  const today = new Date();
  return new Intl.DateTimeFormat(locale.value, {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(today);
}
</script>

<template>
  <cs-widget
    :title="t('dashboard.blog')"
  >
    <template v-if="blogs.length > 0">
      <div
        v-for="blog in blogs"
        :key="blog.id"
        :class="['card', { 'card--no-image': !blog.imageBase64 }]"
      >
        <div class="blog-text">
          <h4 class="blog-title">{{ blog.title }}</h4>
          <p class="blog-excerpt">{{ blog.excerpt }}</p>
          <div class="blog-footer">
            <cs-link
              v-if="blog.link"
              :link="blog.link"
              is-external-link
              target="_blank"
              class="see-more"
              :aria-label="t('dashboard.seeMore')"
            >
              {{ t('dashboard.seeMore') }}
            </cs-link>
          </div>
        </div>
        <div v-if="blog.imageBase64" class="blog-image-wrapper">
          <img :src="blog.imageBase64" alt="" class="blog-image" />
        </div>
      </div>
    </template>

    <div v-else class="card card--tip">
      <p class="date">{{ todayKey() }}</p>
      <p class="subtitle">{{ tipStore.tip?.product_label || '' }}</p>
      <p class="tip">{{ tipStore.tipBody }}</p>
    </div>
  </cs-widget>
</template>

<style scoped>
.card {
  display: flex;
  border-radius: 0.5rem;
  min-height: 14rem;
  max-height: 19rem;
  overflow: hidden;
  background: linear-gradient(
    264.38deg,
    rgba(60, 54, 219, 0.1) 0%,
    rgba(1, 168, 255, 0.1) 100%
  );
}

@media screen and (max-width: 1699px) {
  .card {
    min-height: 13rem;
  }
  .card--tip {
    min-height: 12rem;
  }
}
@media screen and (min-width: 1700px) and (max-width: 1900px) {
  .card {
    min-height: 13.5rem;
  }
  .card--tip {
    min-height: 12rem;
  }
}

@media screen and (min-width: 1910px) and (max-width: 2100px) {
  .card {
    min-height: 16rem;
  }
  .card--tip {
    min-height: 15rem;
  }
}

@media screen and (min-width: 2101px) {
  .card {
    min-height: 19rem;
  }
  .card--tip {
    min-height: 17rem;
  }
}

.card--no-image {
  flex-direction: column;
}

.card--no-image .blog-text {
  padding: 1rem;
}

.card--no-image .blog-excerpt {
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  line-clamp: 4;
  -webkit-box-orient: vertical;
}

.blog-text {
  flex: 1;
  min-width: 0;
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
}

.blog-image-wrapper {
  width: 40%;
  flex-shrink: 0;
}

.blog-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 0 0.5rem 0.5rem 0;
}

.date {
  font-weight: bold;
  font-size: 1.25rem;
  margin-bottom: 0.5rem;
}

.blog-title {
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
  color: var(--el-text-color-primary, #303133);
}

.blog-excerpt {
  font-size: 0.875rem;
  line-height: 1.4;
  margin: 0 0 0.5rem 0;
  color: var(--el-text-color-regular, #606266);
}

.blog-footer {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: auto;
}

.see-more {
  font-size: 0.875rem;
  font-weight: 500;
}

.card--tip {
  flex-direction: column;
  padding: 1rem;
}

.subtitle {
  font-style: italic;
  color: #595959;
}

.tip {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  line-height: 1.4;
  color: var(--el-text-color-regular, #606266);
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 5;
  line-clamp: 5;
  -webkit-box-orient: vertical;
}
</style>
